<h4>S3 bucket replication needs to be manually configured.</h4>
<div class="margin-top">
  You have chosen to configure S3 bucket replication yourself. {{ uiService.productName }} has prepared the replica
  bucket in the recovery environment, and granted access to an IAM role for you to utilize. The details you need to
  complete S3 bucket replication are below.
</div>
<h4 class="margin-top">Configuration Instructions</h4>
<div class="tab margin-top">
  <button
    type="button"
    class="tablinks strong-text"
    [ngClass]="{ active: currentTab == 'console' }"
    (click)="showTab('console')"
  >
    AWS Console
  </button>
  <button
    type="button"
    class="tablinks strong-text"
    [ngClass]="{ active: currentTab == 'cloudformation' }"
    (click)="showTab('cloudformation')"
  >
    CloudFormation
  </button>
  <button
    type="button"
    class="tablinks strong-text"
    [ngClass]="{ active: currentTab == 'terraform' }"
    (click)="showTab('terraform')"
  >
    Terraform
  </button>
</div>

<div>
  @if (currentTab == 'console') {
    <div id="console" class="tabcontent">
      <div class="margin-top">
        <a [href]="uiService.bucketReplicationInstructionsUrl" target="_blank"
          >Click here for detailed instructions on configuring bucket replication</a
        >
      </div>
      <div class="margin-top">When configuring replication, you will need the following details:</div>
      <div class="detail-label margin-top">Destination bucket</div>
      <div class="command-block">
        <button
          class="copy-btn"
          [copy-clipboard]="destinationBucketName"
          (copied)="copyFeedback.onCopied('destBucket')"
        >
          @if (copyFeedback.copiedStates['destBucket']) {
            <svg lucideCheck class="copy-check"></svg>
          } @else {
            <svg lucideCopy></svg>
          }
          {{ copyFeedback.copiedStates['destBucket'] ? 'Copied' : 'Copy' }}
        </button>
        <pre><code>{{ destinationBucketName }}</code></pre>
      </div>
      <div class="detail-label">Replication IAM role</div>
      <div class="command-block">
        <button class="copy-btn" [copy-clipboard]="replicationIamRoleName" (copied)="copyFeedback.onCopied('iamRole')">
          @if (copyFeedback.copiedStates['iamRole']) {
            <svg lucideCheck class="copy-check"></svg>
          } @else {
            <svg lucideCopy></svg>
          }
          {{ copyFeedback.copiedStates['iamRole'] ? 'Copied' : 'Copy' }}
        </button>
        <pre><code>{{ replicationIamRoleName }}</code></pre>
      </div>
      <div class="detail-label">KMS key for encrypted objects</div>
      <div class="command-block">
        <button class="copy-btn" [copy-clipboard]="kmsKeyArn" (copied)="copyFeedback.onCopied('kmsKey')">
          @if (copyFeedback.copiedStates['kmsKey']) {
            <svg lucideCheck class="copy-check"></svg>
          } @else {
            <svg lucideCopy></svg>
          }
          {{ copyFeedback.copiedStates['kmsKey'] ? 'Copied' : 'Copy' }}
        </button>
        <pre><code>{{ kmsKeyArn }}</code></pre>
      </div>
      <div class="static-details margin-top">
        <div><span class="strong-text">Replicate objects encrypted with KMS: </span>Enabled</div>
        <div><span class="strong-text">Replication Time Control (RTC): </span>Enabled</div>
        <div><span class="strong-text">Delete marker replication: </span>Enabled</div>
      </div>
    </div>
  }

  @if (currentTab == 'cloudformation') {
    <div id="cloudformation" class="tabcontent">
      <div class="margin-top">
        Update the CloudFormation resource for the S3 bucket named {{ sourceBucketName }} in your primary environment
        similar to the following. You may need to enable versioning first and then add the replication configuration.
      </div>
      <div class="command-block multiline margin-top">
        <button class="copy-btn" [copy-clipboard]="cfPre.textContent" (copied)="copyFeedback.onCopied('cf')">
          @if (copyFeedback.copiedStates['cf']) {
            <svg lucideCheck class="copy-check"></svg>
          } @else {
            <svg lucideCopy></svg>
          }
          {{ copyFeedback.copiedStates['cf'] ? 'Copied' : 'Copy' }}
        </button>
        <pre #cfPre><code>Type: AWS::S3::Bucket
            Properties:
            BucketName: <span class="highlight">{{ sourceBucketName }}</span>
            ReplicationConfiguration:
            Role: <span class="highlight">{{ replicationIamRoleArn }}</span>
            Rules:
            - Destination:
            Account: <span class="highlight">'{{ targetAccount }}'</span>
            Bucket: <span class="highlight">'{{ destinationBucketArn }}'</span>
            EncryptionConfiguration:
            ReplicaKmsKeyID: <span class="highlight">'{{ kmsKeyArn }}'</span>
            Metrics:
            Status: Enabled
            EventThreshold:
            Minutes: 15
            ReplicationTime:
            Status: Enabled
            Time:
            Minutes: 15
            AccessControlTranslation:
            Owner: Destination
            Status: Enabled
            Filter:
            Prefix: ''
            DeleteMarkerReplication:
            Status: Enabled
            Priority: 1
            SourceSelectionCriteria:
            SseKmsEncryptedObjects:
            Status: Enabled
            VersioningConfiguration:
          Status: Enabled</code></pre>
      </div>
    </div>
  }

  @if (currentTab == 'terraform') {
    <div id="terraform" class="tabcontent">
      <div class="margin-top">
        Update the Terraform resource for the S3 bucket named {{ sourceBucketName }} in your primary environment similar
        to the following:
      </div>
      <div class="command-block multiline margin-top">
        <button class="copy-btn" [copy-clipboard]="tfPre.textContent" (copied)="copyFeedback.onCopied('tf')">
          @if (copyFeedback.copiedStates['tf']) {
            <svg lucideCheck class="copy-check"></svg>
          } @else {
            <svg lucideCopy></svg>
          }
          {{ copyFeedback.copiedStates['tf'] ? 'Copied' : 'Copy' }}
        </button>
        <pre #tfPre><code>resource "aws_s3_bucket" "bucket" &#123;
            bucket   = "<span class="highlight">{{ sourceBucketName }}</span>"
            &#125;
            resource "aws_s3_bucket_versioning" "versioning" &#123;
            bucket = aws_s3_bucket.bucket.id
            versioning_configuration &#123;
            status = "Enabled"
            &#125;
            &#125;
            resource "aws_s3_bucket_replication_configuration" "replication" &#123;
            # Must have bucket versioning enabled first
            depends_on = [aws_s3_bucket_versioning.versioning]
            role   = "<span class="highlight">{{ replicationIamRoleArn }}</span>"
            bucket = aws_s3_bucket.bucket.id
            rule &#123;
            id       = "arpio-replication"
            status   = "Enabled"
            priority = 1
            filter &#123;&#125;
            destination &#123;
            account = "<span class="highlight">{{ targetAccount }}</span>"
            bucket  = "<span class="highlight">{{ destinationBucketArn }}</span>"
            encryption_configuration &#123;
            replica_kms_key_id = "<span class="highlight">{{ kmsKeyArn }}</span>"
            &#125;
            access_control_translation &#123;
            owner = "Destination"
            &#125;
            &#125;
            delete_marker_replication &#123;
            status = "Enabled"
            &#125;
            source_selection_criteria &#123;
            sse_kms_encrypted_objects &#123;
            status = "Enabled"
            &#125;
            &#125;
            &#125;
          &#125;</code></pre>
      </div>
    </div>
  }
</div>
